import { Search } from 'lucide-react'
import type { ReactElement } from 'react'
import { cn } from '@/lib/utils'

export interface SearchFieldProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  /** Rótulo acessível — o campo não tem <label> visível na toolbar. */
  label: string
  className?: string
}

/**
 * SearchField — a busca das toolbars de lista (LUMEN.md §9).
 *
 * Usa a classe `.field` (cápsula, superfície rebaixada) em vez do `Input` do
 * shadcn: este campo é CHROME, não dado. Os formulários continuam a usar o
 * `Input` — misturar os dois vocabulários é o que fazia a toolbar parecer um
 * formulário encaixado no topo da tabela.
 *
 * A busca vive sempre à direita da toolbar, em toda a app.
 *
 * ORÇAMENTO DO PLACEHOLDER — a largura é do design system, o texto é que cede.
 * A cápsula tem 220px fixos e o texto começa depois do ícone (`pl-[34px]`),
 * sobrando ~170px a 13,5px. Um `<input>` NÃO reticencia o placeholder: o que
 * não cabe é cortado a meio da palavra, sem sinal nenhum. Foi assim que
 * "Buscar por nome, slug ou CNPJ" apareceu em produção como "Buscar por nome,
 * slug ou" — a palavra que dizia o mais útil foi a que desapareceu.
 *
 * ⚠️ E VOLTOU A ACONTECER, COM A TROCA DE VOCABULÁRIO PARA "PROFISSIONAL".
 * "Buscar candidato ou vaga" (161,34px, com 8,66px de folga) virou "Buscar
 * profissional ou vaga" e foi para produção a cortar. Medido na tela com
 * `measureText` na fonte real (13,5px "Schibsted Grotesk"): 172,18px de texto
 * para 170px úteis. O que se lia era "Buscar profissional ou va".
 *
 * A régua prática é o placeholder mais longo que a app mostra inteiro:
 * "Buscar nome ou email" (139,33px). Foi "Buscar nome ou vaga" (135,01px) que
 * substituiu o texto cortado — e repara no padrão que o resolveu: quando a
 * ENTIDADE não cabe, o CAMPO cabe. "nome" diz o que se procura sem nomear o que
 * se está a procurar, e é por isso que as duas telas irmãs o usam. O padrão da app é
 * `Buscar {campo} ou {campo}` — sem o "por", que ocupa quatro caracteres e não
 * informa nada. Deixar o campo crescer NÃO é a saída: a geometria da toolbar é
 * a mesma em todas as listas, e mudá-la por causa de duas telas trocaria um
 * defeito de texto por uma divergência de layout.
 */
export function SearchField({
  value,
  onChange,
  placeholder = 'Buscar',
  label,
  className,
}: SearchFieldProps): ReactElement {
  return (
    <div className={cn('relative w-[220px]', className)}>
      <Search
        aria-hidden="true"
        className="text-ink-4 pointer-events-none absolute left-3 top-1/2 size-[15px] -translate-y-1/2"
      />
      <input
        type="search"
        className="field pl-[34px]"
        aria-label={label}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  )
}
